import context from '@truckdown/systems';
import { IInitializer, IUserContext } from "@truckdown/systems";
import { defineAsyncComponent } from 'vue';

import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import icon from "./Icon/Icon.vue";
import lazy from "./Lazy/Lazy.vue";
import error from "./Error/Error.vue";
import loading from "./Loading/Loading.vue";
import modal from "./Modal/Modal.vue";
import headerIcons from "./HeaderIcons/HeaderIcons.vue";

import VueSmoothScroll from 'vue3-smooth-scroll';

import { IAppUserContext } from '../IAppUserContext';

class ComponentInitializer implements IInitializer {
    name: string = 'ComponentInitializer';
    order: number = 0;

    initialize(context: IUserContext): Promise<void> {
        let app = (context as IAppUserContext).app;
        app.use(VueSmoothScroll, {
            duration: 400,
            updateHistory: true
        });

        app.component('fa-icon', FontAwesomeIcon);
        app.component('icon', icon);
        app.component('lazy', lazy);
        app.component('error', error);
        app.component('loading', loading);
        app.component('modal', modal);
        app.component('header-icons', headerIcons);

        app.component('header-menu-list', defineAsyncComponent({
            loader: () => import(/* webpackChunkName: "comp-systems-header-menu-list" */ './HeaderIcons/HeaderMenuList/HeaderMenuList.vue'),
            loadingComponent: loading,
            errorComponent: error,
            delay: 200, //default 200 - does not work
            timeout: 3000, //default 3000 - Infinity
            suspensible: false
        }));
        app.component('setup-progress', defineAsyncComponent({
            loader: () => import(/* webpackChunkName: "comp-systems-setup-progress" */ './SetupProgress/SetupProgress.vue'),
            loadingComponent: loading,
            errorComponent: error,
            delay: 200, //default 200 - does not work
            timeout: 5000, //default 3000 - Infinity
            suspensible: false
        }));
        app.component('control-panel', defineAsyncComponent({
            loader: () => import(/* webpackChunkName: "comp-systems-control-panel" */ './ControlPanel/ControlPanel.vue'),
            loadingComponent: loading,
            errorComponent: error,
            delay: 200, //default 200 - does not work
            timeout: 5000, //default 3000 - Infinity
            suspensible: false
        }));

        app.component('captcha', defineAsyncComponent({
            loader: () => import(/* webpackChunkName: "comp-systems-captcha" */ './Captcha/Captcha.vue'),
            loadingComponent: loading,
            errorComponent: error,
            delay: 200, //default 200 - does not work
            timeout: 5000, //default 3000 - Infinity
            suspensible: false
        }));

        app.component('notification-component', defineAsyncComponent({
            loader: () => import(/* webpackChunkName: "comp-systems-notification-component" */ './NotificationComponent/NotificationComponent.vue'),
            loadingComponent: loading,
            errorComponent: error,
            delay: 200, //default 200 - does not work
            timeout: 5000, //default 3000 - Infinity
            suspensible: false
        }));

        app.component('side-nav', defineAsyncComponent({
            loader: () => import(/* webpackChunkName: "comp-systems-side-nav" */ './SideNav/SideNav.vue'),
            loadingComponent: loading,
            errorComponent: error,
            delay: 200, //default 200 - does not work
            timeout: 5000, //default 3000 - Infinity
            suspensible: false
        }));

        app.component('create-task', defineAsyncComponent({
            loader: () => import(/* webpackChunkName: "comp-systems-create-task" */ './CreateTask/CreateTask.vue'),
            loadingComponent: loading,
            errorComponent: error,
            delay: 200, //default 200 - does not work
            timeout: 5000, //default 3000 - Infinity
            suspensible: false
        }));

        const datePicker = defineAsyncComponent({
            loader: async () => {
                try {
                    const module = await import(/* webpackChunkName: "comp-systems-vue-date-picker" */ '@vuepic/vue-datepicker');
                    return module.VueDatePicker;
                }
                catch (err) {
                    console.log(err);
                    console.log('Error loading VueDatePicker module.');
                    return {};
                }
            },
            loadingComponent: loading,
            errorComponent: error,
            delay: 200, //default 200 - does not work
            timeout: 5000, //default 3000 - Infinity
            suspensible: false
        });

        //registered with two names - VueDatePicker is for backwards compatibility when
        //using the component inside a vuew template.  Does not work when loading via html.
        app.component('VueDatePicker', datePicker);
        app.component('vue-date-picker', datePicker);

        app.component('v-lazy-image', defineAsyncComponent({
            loader: () => import(/* webpackChunkName: "comp-systems-lazy-image" */ 'v-lazy-image'),
            loadingComponent: loading,
            errorComponent: error,
            delay: 200, //default 200 - does not work
            timeout: 5000, //default 3000 - Infinity
            suspensible: false
        }));

        app.component('new-conversation', defineAsyncComponent({
            loader: () => import(/* webpackChunkName: "comp-systems-new-conversation" */ './NewConversation/NewConversation.vue'),
            loadingComponent: loading,
            errorComponent: error,
            delay: 200, //default 200 - does not work
            timeout: 5000, //default 3000 - Infinity
            suspensible: false
        }));

        app.component('generic-pager', defineAsyncComponent({
            loader: () => import(/* webpackChunkName: "comp-systems-generic-pager" */ './GenericPager/GenericPager.vue'),
            loadingComponent: loading,
            errorComponent: error,
            delay: 200, //default 200 - does not work
            timeout: 5000, //default 3000 - Infinity
            suspensible: false
        }));

        app.component('relationship-lookup', defineAsyncComponent({
            loader: () => import(/* webpackChunkName: "comp-systems-relationship-lookup" */ './RelationshipLookup/RelationshipLookup.vue'),
            loadingComponent: loading,
            errorComponent: error,
            delay: 200, //default 200 - does not work
            timeout: 5000, //default 3000 - Infinity
            suspensible: false
        }));

        app.component('context-info', defineAsyncComponent({
            loader: () => import(/* webpackChunkName: "comp-systems-context-info" */ './HeaderIcons/HeaderMenuList/ContextInfo/ContextInfo.vue'),
            loadingComponent: loading,
            errorComponent: error,
            delay: 200, //default 200 - does not work
            timeout: 5000, //default 3000 - Infinity
            suspensible: false
        }));

        return Promise.resolve();
    }
}

context.registerService({
    name: 'IInitializer',
    activator: async () => {
        return new ComponentInitializer();
    }
});
